const _prefetchCache = {};

class MainProduct extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.defaultProductHandle = this.dataset.productHandle;

    this.onPackClick = this.onPackClick.bind(this);
    this.onPurchaseChange = this.onPurchaseChange.bind(this);
  }

  connectedCallback() {
    this.initStateFromDom();
    this.bindEvents();
    this.applyStateToDom();
    this.dispatchSelectionChange();
    this.prefetchOtherProducts();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  initStateFromDom() {
    this.activeProductHandle =
      this.dataset.productHandle || this.defaultProductHandle || "";

    this.activeVariantId = this.dataset.activeVariantId || null;
    this.activeSellingPlanId = this.dataset.activeSellingPlanId || null;
    this.activePurchaseOption =
      this.dataset.activePurchaseOption ||
      (this.activeSellingPlanId ? "subscribe" : "onetime");

    if (!this.activeVariantId) {
      const checked = this.querySelector(
        '[data-role="pdp-pack-size"] input[name^="variant-select"]:checked'
      );
      if (checked) {
        this.activeProductHandle =
          checked.dataset.productHandle || this.activeProductHandle;
        this.activeVariantId = checked.value || null;
      }
    }

    if (
      !this.activeSellingPlanId &&
      this.dataset.activePurchaseOption !== "onetime"
    ) {
      const sellingPlanInput = this.querySelector('[data-role="selling-plan-id"], #selling-plan-id');
      if (sellingPlanInput && sellingPlanInput.value) {
        this.activeSellingPlanId = sellingPlanInput.value;
        this.activePurchaseOption = "subscribe";
      }
    }
  }

  bindEvents() {
    this.unbindEvents();

    this.packButtons = Array.from(this.querySelectorAll(".pack-size-option"));
    this.purchaseRadios = Array.from(
      this.querySelectorAll('[data-purchase-type]')
    );

    this.packButtons.forEach((btn) => {
      btn.addEventListener("click", this.onPackClick);
    });

    this.purchaseRadios.forEach((radio) => {
      radio.addEventListener("change", this.onPurchaseChange);
    });
  }

  unbindEvents() {
    if (this.packButtons) {
      this.packButtons.forEach((btn) => {
        btn.removeEventListener("click", this.onPackClick);
      });
    }
    if (this.purchaseRadios) {
      this.purchaseRadios.forEach((radio) => {
        radio.removeEventListener("change", this.onPurchaseChange);
      });
    }
  }

  applyStateToDom() {
    const sellingPlanInput = this.querySelector('[data-role="selling-plan-id"], #selling-plan-id');
    const subscribeRadio = this.querySelector('[data-purchase-type="subscribe"]');
    const oneTimeRadio = this.querySelector('[data-purchase-type="onetime"]');
    const isSubscribe =
      this.activePurchaseOption === "subscribe" && !!this.activeSellingPlanId;

    if (sellingPlanInput) {
      sellingPlanInput.value = this.activeSellingPlanId || "";
    }

    if (subscribeRadio) {
      subscribeRadio.checked = isSubscribe;
    }

    if (oneTimeRadio) {
      oneTimeRadio.checked = !isSubscribe && this.activePurchaseOption !== "trial";
    }

    const priceCurrentEl = this.querySelector("#pdp-price .price-current");
    const priceCompareEl = this.querySelector("#pdp-price .price-compare");

    const subPriceEl = this.querySelector('[data-role="sub-price"]');
    const oneTimeBox = this.querySelector('[data-role="one-time-price-box"]');

    const subPrice =
      subPriceEl &&
      (subPriceEl.dataset.subPrice || subPriceEl.textContent.trim());

    let oneTimePrice = "";
    let oneTimeCompare = "";

    if (oneTimeBox) {
      oneTimePrice =
        oneTimeBox.dataset.oneTimePrice ||
        (
          oneTimeBox.querySelector('[data-role="one-time-active"]') ||
          oneTimeBox.querySelector("span")
        )?.textContent.trim() ||
        "";

      oneTimeCompare =
        oneTimeBox.dataset.oneTimeCompare ||
        (
          oneTimeBox.querySelector('[data-role="one-time-compare"]') || null
        )?.textContent.trim() ||
        "";

      if (!oneTimeCompare) {
        oneTimeCompare = oneTimePrice;
      }
    }

    let activePrice = "";

    if (isSubscribe && subPrice) {
      activePrice = subPrice;
    } else {
      activePrice = oneTimePrice || subPrice || "";
    }

    let comparePrice = oneTimeCompare || activePrice;

    if (priceCurrentEl) {
      priceCurrentEl.textContent = activePrice;
    }

    const toNumber = (str) => {
      if (!str) return 0;
      const clean = str.replace(/[^0-9.]/g, "");
      const val = parseFloat(clean);
      return isNaN(val) ? 0 : val;
    };

    if (priceCompareEl) {
      const activeVal = toNumber(activePrice);
      const compareVal = toNumber(comparePrice);

      if (!comparePrice || compareVal <= activeVal) {
        priceCompareEl.textContent = "";
        priceCompareEl.classList.add("hidden");
      } else {
        priceCompareEl.textContent = comparePrice;
        priceCompareEl.classList.remove("hidden");
      }
    }

    const atcPriceEl = this.querySelector(".atc-price");
    if (atcPriceEl && activePrice) {
      atcPriceEl.textContent = activePrice;
    }

    this.querySelectorAll("[data-purchase-type]").forEach((radio) => {
      const fill = radio.nextElementSibling?.firstElementChild;
      if (fill) fill.style.opacity = radio.checked ? "1" : "0";

      const box = radio.closest("[data-plan-box]");
      if (box) {
        box.classList.toggle("border-blue-main", radio.checked);
        box.classList.toggle("border-black/15", !radio.checked);
      }
    });
  }

  htmlToElement(html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  }

  dispatchSelectionChange() {
    window.dispatchEvent(
      new CustomEvent("main-product:selection-change", {
        detail: {
          productHandle: this.activeProductHandle,
          variantId: this.activeVariantId,
          sellingPlanId: this.activeSellingPlanId,
          purchaseOption: this.activePurchaseOption,
        },
      })
    );
  }

  onPackClick(event) {
    const input = event.currentTarget.querySelector(
      "input[data-product-handle]"
    );
    if (!input) return;

    this.activeProductHandle =
      input.dataset.productHandle || this.defaultProductHandle || "";
    this.activeVariantId = input.value || null;

    this.dispatchSelectionChange();
    this.updateProductDetailsHTML();
  }

  onPurchaseChange(event) {
    const target = event.target;
    const sellingPlanInput = this.querySelector('[data-role="selling-plan-id"], #selling-plan-id');
    const subscribeRadio = this.querySelector('[data-purchase-type="subscribe"]');
    const oneTimeRadio = this.querySelector('[data-purchase-type="onetime"]');

    if (target.dataset.purchaseType === "trial") {
      this.activeSellingPlanId = null;
      this.activePurchaseOption = "trial";
      if (sellingPlanInput) sellingPlanInput.value = "";
      this.dispatchSelectionChange();
      return;
    }

    if (target.dataset.purchaseType === "subscribe") {
      const planIdFromRadio = target.dataset.sellingPlanId || "";
      this.activeSellingPlanId = planIdFromRadio || null;
      this.activePurchaseOption = this.activeSellingPlanId
        ? "subscribe"
        : "onetime";

      if (sellingPlanInput) {
        sellingPlanInput.value = planIdFromRadio;
      }

      if (subscribeRadio) subscribeRadio.checked = true;
      if (oneTimeRadio) oneTimeRadio.checked = false;
    }

    if (target.dataset.purchaseType === "onetime") {
      this.activeSellingPlanId = null;
      this.activePurchaseOption = "onetime";

      if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }

      if (subscribeRadio) subscribeRadio.checked = false;
      if (oneTimeRadio) oneTimeRadio.checked = true;
    }

    this.applyStateToDom();
    this.dispatchSelectionChange();
  }

  buildRequestPath() {
    const params = new URLSearchParams();
    params.set("sections", this.sectionId);

    if (this.activeVariantId) {
      params.set("variant", this.activeVariantId);
    }

    if (this.activeSellingPlanId) {
      params.set("selling_plan", this.activeSellingPlanId);
    }

    params.set(
      "purchase_option",
      this.activeSellingPlanId ? "subscribe" : "onetime"
    );

    return `/products/${this.activeProductHandle}?${params.toString()}`;
  }

  /* ── Prefetch other products' section HTML + preload their gallery images ── */

  prefetchOtherProducts() {
    const inputs = this.querySelectorAll(
      '.pack-size-option input[data-product-handle]'
    );
    const sectionId = this.sectionId;
    const currentHandle = this.activeProductHandle;

    inputs.forEach((input) => {
      const handle = input.dataset.productHandle;
      if (!handle || handle === currentHandle || _prefetchCache[handle]) return;

      // Mark as pending so we don't double-fetch
      _prefetchCache[handle] = "pending";

      const params = new URLSearchParams();
      params.set("sections", sectionId);
      if (input.value) params.set("variant", input.value);

      const url = `/products/${handle}?${params.toString()}`;

      fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          if (!json || !json[sectionId]) return;

          const html = json[sectionId];
          _prefetchCache[handle] = html;

          // Parse and preload all gallery images (src = 800w fallback)
          const doc = new DOMParser().parseFromString(html, "text/html");
          const gallery = doc.querySelector("#pdp-gallery");
          if (!gallery) return;

          gallery.querySelectorAll("img[src]").forEach((img) => {
            new Image().src = img.getAttribute("src");
          });
        })
        .catch(() => {
          delete _prefetchCache[handle];
        });
    });
  }

  /* ── Update section HTML (uses cache when available) ── */

  async updateProductDetailsHTML() {
    if (!this.sectionId || !this.activeProductHandle) return;

    const focusEl = document.activeElement;
    let html;

    const cached = _prefetchCache[this.activeProductHandle];
    if (cached && typeof cached === "string" && cached !== "pending") {
      html = cached;
    } else {
      const rawPathWithParams = this.buildRequestPath();
      const rawResponse = await fetch(rawPathWithParams);
      if (!rawResponse.ok) return;
      const response = await rawResponse.json();
      html = response[this.sectionId];
    }

    const sectionRoot = this.htmlToElement(html);
    if (!sectionRoot) return;

    const newMainProduct =
      sectionRoot.querySelector("main-product") ||
      sectionRoot.querySelector(
        `[data-section-id="${this.sectionId}"] main-product`
      );

    if (!newMainProduct) return;

    newMainProduct.dataset.sectionId = this.sectionId;
    newMainProduct.dataset.productHandle = this.activeProductHandle;

    if (this.activeVariantId) {
      newMainProduct.dataset.activeVariantId = this.activeVariantId;
    }

    // Pass current purchase-option state so the new element applies correct pricing
    if (this.activeSellingPlanId) {
      newMainProduct.dataset.activeSellingPlanId = this.activeSellingPlanId;
      newMainProduct.dataset.activePurchaseOption = "subscribe";
    } else {
      newMainProduct.dataset.activePurchaseOption = "onetime";
    }

    this.replaceWith(newMainProduct);

    if (focusEl && focusEl.id) {
      const next = document.getElementById(focusEl.id);
      if (next) next.focus();
    }
  }
}

if (!customElements.get("main-product")) {
  customElements.define("main-product", MainProduct);
}
